import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { FeedbackEvent } from '@bonusly/common-feedback';
import { GiveBoxEvent } from '@bonusly/common-give-box';
import {
	Avatar,
	Box,
	ChatAltOutline,
	ClappingHandsOutline,
	ClappingHandsSolid,
	DotsHorizontalOutline,
	Flex,
	HStack,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	rem,
	Text,
	Tooltip,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { useCloseNavbarPanels } from '../../../../../../../hooks/use-navbar-panels';
import { ProfileLink } from '../../../../../../../modules/profile/components/profile-link';
import { useDaysSinceLastInteraction, useLastInteractedDate } from '../hooks/use-interaction';
import { Person } from '../types';

interface Props {
	person: Person;
}

export const PersonStats: FC<Props> = ({ person }) => {
	const { t } = useTranslation('tracker_widget');
	const dispatch = useDispatch();
	const { is_manager } = useCurrentUser();
	const { track } = useAnalytics();
	const canAccessAdvice = usePermission(Permission.CanAccessAdvice);
	const closeAllPanels = useCloseNavbarPanels();

	const lastInteractionDate = useLastInteractedDate(person);
	const daysSinceInteraction = useDaysSinceLastInteraction(lastInteractionDate);
	const lastRecognizedText = useMemo(() => {
		if (!lastInteractionDate) {
			return t('person_list.never_interacted');
		} else if (daysSinceInteraction === 0) {
			return t('person_list.last_interacted_today');
		} else {
			return t('person_list.last_interacted', { count: daysSinceInteraction });
		}
	}, [daysSinceInteraction, lastInteractionDate, t]);

	const recognize = () => {
		track(AnalyticsEvent.TrackerWidget.GiveRecognitionButtonClicked);
		dispatch(GiveBoxEvent.Open, {
			recipient: {
				id: person.id,
				displayName: person.display_name,
				profilePic: person.profile_pic_url,
				username: person.username,
			},
		});
	};

	const giveFeedback = () => {
		track(AnalyticsEvent.TrackerWidget.GiveFeedbackButtonClicked);
		dispatch(FeedbackEvent.OpenUnsolicited, {
			defaultUsers: [
				{
					id: person.id,
					imageUrl: person.profile_pic_url,
					name: person.display_name,
					username: person.username,
				},
			],
		});
	};

	return (
		<ProfileLink userId={person.id} onClick={closeAllPanels} _hover={{ textDecoration: 'none' }} display="block">
			<Flex
				role="group"
				alignItems="center"
				py={2}
				px={rem(10)}
				mx={rem(-12)}
				h={rem(68)}
				borderRadius="lg"
				_hover={{ bg: 'secondary.bgAlpha' }}
				transition="background 0.15s ease"
				cursor="pointer"
			>
				<Box flexShrink={0}>
					<Avatar name={person.display_name} src={person.profile_pic_url} size="md" />
				</Box>

				<Box w={rem(12)} flexShrink={0} />

				<Box flex={1} minW={0}>
					<Text size="md" variant="medium" color="text.default" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" mb={0}>
						{person.display_name}
					</Text>
					<Text size="sm" color="text.softer" mb={0}>
						{lastRecognizedText}
					</Text>
				</Box>

				<Box
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
					flexShrink={0}
				>
					{is_manager ? (
						<Box color="info.icon" _groupHover={{ color: 'text.default' }} transition="color 0.15s ease">
							<Menu placement="right-start">
								<MenuButton
									as={IconButton}
									aria-label={t('person_list.actions_menu_aria_label', { name: person.display_name })}
									variant="ghost"
									size="sm"
									color="inherit"
									icon={<DotsHorizontalOutline size="sm" />}
									_hover={{ backgroundColor: 'secondary.bgAlpha' }}
									_active={{ backgroundColor: 'secondary.bgAlpha' }}
									_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'md', outlineOffset: '-2px' }}
								/>
								<Portal>
									<MenuList
										width="fit-content"
										zIndex={99999}
										px={rem(6)}
										py={rem(8)}
										sx={{
											'& > *:not(:last-child)': { mb: rem(4) },
											'& [role="menuitem"]': {
												minHeight: rem(40),
											},
											'& [role="menuitem"]:focus-visible': {
												outline: '2px solid',
												outlineColor: 'border.focus',
												outlineOffset: '-2px',
												borderRadius: 'sm',
												backgroundColor: 'bg.surface',
											},
										}}
									>
										<MenuItem onClick={recognize}>
											<HStack gap={2}>
												<ClappingHandsOutline />
												<Text>{t('person_list.give_recognition')}</Text>
											</HStack>
										</MenuItem>

										{canAccessAdvice && (
											<MenuItem onClick={giveFeedback}>
												<HStack gap={2}>
													<ChatAltOutline />
													<Text>{t('person_list.give_feedback')}</Text>
												</HStack>
											</MenuItem>
										)}
									</MenuList>
								</Portal>
							</Menu>
						</Box>
					) : (
						<Tooltip hasArrow label={t('person_list.give_recognition')} openDelay={300} placement="top">
							<IconButton
								aria-label={t('person_list.give_recognition_aria_label', { name: person.display_name })}
								backgroundColor="primary.bgAlpha"
								color="primary.fg"
								borderRadius="md"
								size="sm"
								icon={<ClappingHandsSolid h={5} w={5} />}
								onClick={recognize}
								_hover={{ backgroundColor: 'primary.bgAlphaHover' }}
								_active={{ backgroundColor: 'primary.bgAlphaHover' }}
								_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'md', outlineOffset: '-2px' }}
							/>
						</Tooltip>
					)}
				</Box>
			</Flex>
		</ProfileLink>
	);
};
